import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@core/http";
import { signIn } from "./api";
import { saveValidatorSession } from "./session";
import { Shell, Brand } from "./AccessLayout";
import {
  BigButton,
  CheckToggle,
  Field,
  Notice,
  TextButton,
} from "./primitives";
import "./access.css";

// SPEC-301 §3 · `validador.jsx:124-154` (`Login`) + `:651-665` (`LoginErr`,
// que el catálogo de revisión trata como pantalla aparte pero que acá es el
// estado `error` de esta misma pantalla — `context/prototype/screens/
// validator.md` §1).
//
// A diferencia de `portal/access/LoginScreen.tsx`, esta pantalla SÍ lleva el
// checkbox "Mantener la sesión iniciada" (RF-VAL-03, activo por defecto): es
// justamente el RF que el portal no tiene y que originó ese comentario ahí.
const MESSAGES = {
  invalidCredentials:
    "Usuario o contraseña incorrectos. Tras cinco intentos la cuenta se bloquea.",
  accountLockedFallback:
    "Cuenta bloqueada temporalmente. Vuelve a intentarlo más tarde.",
  network: "No se pudo conectar con el servidor. Intenta de nuevo.",
};

function resolveSignInError(err: unknown): {
  message: string;
  locksAccount: boolean;
} {
  if (!(err instanceof ApiError)) {
    return { message: MESSAGES.network, locksAccount: false };
  }
  switch (err.status) {
    case 401:
      return { message: MESSAGES.invalidCredentials, locksAccount: false };
    case 429: {
      const retryAfterSeconds = err.problem?.retryAfterSeconds;
      const message =
        retryAfterSeconds === undefined
          ? MESSAGES.accountLockedFallback
          : `Cuenta bloqueada temporalmente. Intenta en ${String(Math.ceil(retryAfterSeconds / 60))} minutos.`;
      return { message, locksAccount: true };
    }
    default:
      return { message: MESSAGES.network, locksAccount: false };
  }
}

export function LoginScreen() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [keepSession, setKeepSession] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const disabled = locked || submitting;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (disabled) return;
    setError(null);
    setSubmitting(true);
    try {
      const session = await signIn(username, password, keepSession);
      saveValidatorSession(
        {
          token: session.token,
          expiresAt: session.expiresAt,
          userName: session.user.name ?? null,
          placeName: session.place.name,
          placeCity: session.place.city ?? null,
          placeType: session.place.type,
        },
        keepSession,
      );
      if (session.mustChangePassword) {
        // SPEC-301 §6: "un usuario con cambio obligatorio pendiente solo
        // accede a esta pantalla" (la de contraseña nueva). No hay endpoint
        // autenticado publicado para el validador que cumpla ese cambio con
        // la sesión recién creada (el equivalente del portal es
        // `changeMandatoryPassword`, bajo `/v1/portal/**`, no `/v1/validator/
        // **`): `NewPasswordScreen` solo sabe hablar con `PUT
        // /v1/validator/access/password`, que exige el `token` del correo de
        // recuperación (`50-validator.yaml:58`) y esa ruta ni siquiera está
        // montada hoy. El desvío queda declarado como incertidumbre en el
        // reporte: la navegación es correcta, el envío del formulario no se
        // puede ejercitar de punta a punta todavía.
        navigate("/validator/access-new-password", { replace: true });
        return;
      }
      // SPEC-302 ya existe: el destino tras un login exitoso sin cambio
      // obligatorio pendiente es el inicio del validador, no una
      // confirmación inline (ese andamiaje era provisional a SPEC-301).
      navigate("/validator/home", { replace: true });
    } catch (err) {
      const resolved = resolveSignInError(err);
      setError(resolved.message);
      if (resolved.locksAccount) setLocked(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Shell>
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="sc-screen"
      >
        <Brand sub="Validador de beneficios" />
        <div className="sc-spacer-42" />
        <fieldset
          disabled={disabled}
          style={{ border: "none", padding: 0, margin: 0 }}
        >
          {error ? <Notice>{error}</Notice> : null}
          <Field
            label="Usuario"
            value={username}
            onChange={setUsername}
            placeholder="kvera"
            autoComplete="username"
            error={Boolean(error)}
          />
          <Field
            label="Contraseña"
            kind="password"
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            autoComplete="current-password"
            error={Boolean(error)}
          />
          <CheckToggle checked={keepSession} onChange={setKeepSession}>
            Mantener la sesión iniciada
          </CheckToggle>
          <BigButton type="submit" disabled={disabled}>
            {submitting ? "Ingresando…" : "Ingresar"}
          </BigButton>
        </fieldset>
        <TextButton
          onClick={() => {
            navigate("/validator/access-recover");
          }}
        >
          Olvidé mi contraseña
        </TextButton>
        <div className="sc-flex-1" />
        <div className="sc-support">
          Acceso exclusivo para el personal de comercios aliados.
          <br />
          Soporte Star Circle · 1800 782 472
        </div>
      </form>
    </Shell>
  );
}
